import React, { useEffect, useState } from 'react';
import type { DataSourceMode } from '../services/DataBridge';
import { LiveBmsService, type LiveBmsStatus } from '../services/LiveBmsService';

interface DataSourceModeSelectorProps {
  mode: DataSourceMode;
  onChange: (mode: DataSourceMode) => void | Promise<void>;
  className?: string;
}

const sourceLabel = (mode: DataSourceMode, liveReady: boolean) => {
  if (mode === 'energyplus') return '테스트 · EnergyPlus';
  if (mode === 'live') return liveReady ? '운영 · 현장 BEMS' : '운영 · 현장 BEMS 대기';
  return '운영 · 과거 BEMS';
};

export const DataSourceModeSelector: React.FC<DataSourceModeSelectorProps> = ({
  mode,
  onChange,
  className = '',
}) => {
  const [liveStatus, setLiveStatus] = useState<LiveBmsStatus>(() => LiveBmsService.getStatus());
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  useEffect(() => {
    LiveBmsService.start();
    return LiveBmsService.subscribe(setLiveStatus);
  }, []);

  const selectedMode = mode === 'simulation' ? 'historical' : mode;
  const liveReady = liveStatus.connected && liveStatus.pointCount > 0;

  const handleChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextMode = event.target.value as DataSourceMode;
    if (nextMode === selectedMode) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      await onChange(nextMode);
    } catch (error) {
      setSwitchError(error instanceof Error ? error.message : '데이터 소스를 전환하지 못했습니다.');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <label className={`flex h-11 min-w-[190px] items-center gap-2 rounded-2xl border border-[#dfe3e6] bg-[#f7f8f8] px-3 py-1.5 ${className}`}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${
        selectedMode === 'energyplus'
          ? 'bg-[#7050a5]'
          : selectedMode === 'live' && liveReady
            ? 'bg-[#22a06b] animate-pulse'
            : selectedMode === 'live'
              ? 'bg-[#d6a44a]'
              : 'bg-[#08777d]'
      }`} />
      <span className="sr-only">대시보드 데이터 소스</span>
      <select
        value={selectedMode}
        onChange={handleChange}
        disabled={switching}
        title={selectedMode === 'live' && !liveReady
          ? '현장 센서 데이터 수신을 기다리고 있습니다.'
          : sourceLabel(selectedMode, liveReady)}
        className="min-w-0 flex-1 cursor-pointer bg-transparent text-[11px] font-semibold text-[#3e444b] outline-none disabled:cursor-wait disabled:opacity-60"
      >
        <option value="historical">운영 · 과거 BEMS</option>
        <option value="energyplus">테스트 · EnergyPlus</option>
        <option value="live">운영 · 현장 BEMS{liveReady ? '' : ' (수신 대기)'}</option>
      </select>
      {switching && <i className="fa-solid fa-circle-notch fa-spin text-[10px] text-[#08777d]" />}
      {!switching && switchError && (
        <i className="fa-solid fa-triangle-exclamation text-[10px] text-[#b33f51]" title={switchError} aria-label={switchError} />
      )}
    </label>
  );
};
